import { ArrowUpRight, CalendarClock, Gift } from "lucide-react";
import { SEO } from "../components/SEO";
import { BookmakerLogo } from "../components/BookmakerCard";
import { DemoBadge, EmptyState, SectionHeading } from "../components/ui";
import { useCountry } from "../context/CountryContext";
import { bookmakers, promotions } from "../data/demo";

export function PromotionsPage() {
  const { country } = useCountry();
  const filtered =
    country.code === "global"
      ? promotions
      : promotions.filter((promotion) =>
          promotion.countries.includes(country.code),
        );
  return (
    <div className="page-shell py-10 sm:py-14">
      <SEO
        title={`Betting promotions for ${country.name}`}
        description="Compare country-specific bookmaker promotions with eligibility, expiry and clear affiliate disclosures."
        path="/promotions"
      />
      <SectionHeading
        headingLevel={1}
        eyebrow="Offers & boosts"
        title={`Promotions for ${country.name}`}
        description="Read the eligibility and operator terms before opting in. Offers can change or end early."
      />
      {filtered.length ? (
        <div className="grid gap-4 md:grid-cols-2">
          {filtered.map((promotion) => {
            const bookmaker = bookmakers.find(
              (item) => item.id === promotion.bookmakerId,
            )!;
            const url =
              country.code === "global"
                ? undefined
                : bookmaker.affiliateUrls[country.code];
            return (
              <article
                className="surface lift rounded-2xl p-6"
                key={promotion.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <BookmakerLogo bookmaker={bookmaker} />
                  <Gift className="text-lime" />
                </div>
                <h2 className="display mt-5 text-xl font-black">
                  {promotion.title}
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted">
                  {promotion.description}
                </p>
                <div className="mt-4 rounded-xl bg-white/[.035] p-4 text-xs leading-5">
                  <div>
                    <strong>Eligibility:</strong> {promotion.eligibility}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-muted">
                    <CalendarClock size={14} />
                    Sample expiry:{" "}
                    {new Date(promotion.expiresAt).toLocaleDateString()}
                  </div>
                  <div className="mt-2 text-muted">{promotion.terms}</div>
                </div>
                {url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="nofollow sponsored noopener"
                    className="focus-ring mt-5 flex items-center justify-center gap-2 rounded-xl bg-lime px-4 py-3 text-sm font-extrabold"
                  >
                    View offer <ArrowUpRight size={15} />
                  </a>
                ) : (
                  <div className="mt-5 rounded-xl border border-soft p-3 text-center text-xs text-muted">
                    Choose a supported country to open the affiliate
                    destination.
                  </div>
                )}
                <p className="mt-3 text-center text-[10px] text-muted">
                  Affiliate link · We may earn a commission.
                </p>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="No verified promotions"
          description="We do not show an offer in your country until eligibility and destination details are verified."
        />
      )}
      <div className="mt-5">
        <DemoBadge />
      </div>
    </div>
  );
}
